import { useCallback, useMemo, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { InvoiceNavigationCard } from "@/components/InvoiceNavigationCard"
import { ReprocessConfirmDialog } from "@/components/ReprocessConfirmDialog"
import { DeleteConfirmDialog } from "@/components/DeleteConfirmDialog"
import { DocumentViewer } from "@/components/DocumentViewer"
import { useFileViewer, useFieldEditor, useZoom } from "@/hooks"
import { ClientAssignmentCard } from "./components/ClientAssignmentCard"
import { InvoiceFieldsPanel } from "./components/InvoiceFieldsPanel"
import type { LineItemAction } from "./components/InvoiceFieldsPanel"
import { showErrorToast, showSuccessToast, showLearningToast } from "@/hooks/use-toast"
import { updateInvoiceLineItems, updateLineItemErpMetadata, restoreMergedLineItems, reprocessInvoice } from "@/services/invoiceService"
import { apiClient } from "@/lib/api"
import { Undo2 } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import type { CurrencyRepresentation, Invoice } from "./types"
import { REQUIRED_INVOICE_FIELDS, INVOICE_FIELD_LABELS } from "./fieldConstants"
import { useInvoiceType } from "./hooks/useInvoiceType"

interface LineItemErpMetadata {
  deal_name?: number | null
  acc_assignment?: number | null
  analytic_parts?: Array<{
    acc: string
    level1?: string
    level1_name?: string
    level2?: string
    level2_name?: string
    level3?: string
    level3_name?: string
  }>
  without_account_assignment?: boolean
  without_account_assignment_reason?: "dma_auto" | "manual" | null
}

const REVIEW_SESSIONS_KEY = "review_sessions"

interface ReviewContext {
  type: "selected" | "all_pending"
  invoiceIds: string[]
}

interface ReviewSession {
  id: string
  context: ReviewContext
  createdAt: number
  expiresAt: number
  initialTotal: number
  completedCount: number
}

const getReviewSession = (sessionId: string): ReviewSession | null => {
  try {
    const stored = localStorage.getItem(REVIEW_SESSIONS_KEY)
    if (!stored) return null

    const sessions: Record<string, ReviewSession> = JSON.parse(stored)
    const session = sessions[sessionId]

    if (!session || session.expiresAt < Date.now()) {
      return null
    }

    const normalizedCompleted = typeof session.completedCount === "number" ? session.completedCount : 0
    const normalizedInitialTotal =
      typeof session.initialTotal === "number" && session.initialTotal > 0
        ? session.initialTotal
        : normalizedCompleted + session.context.invoiceIds.length

    return {
      ...session,
      initialTotal: normalizedInitialTotal,
      completedCount: normalizedCompleted
    }
  } catch {
    return null
  }
}

interface InvoiceReviewSectionProps {
  invoice?: Invoice
  isProcessing?: boolean
  onFieldUpdate?: (fieldId: string, newValue: string) => void
  onFieldPatch?: (updatedField: Record<string, unknown>) => void
  onInvoiceRefresh?: () => void
  onInvoiceRefreshSilent?: () => void
  onProcessingStarted?: () => void
  onRepresentationUpdate?: (updatedRepr: CurrencyRepresentation) => void
  onLineItemsUpdated?: (extractedData: any[], representation: any | null, additionalMetadata?: any) => void
  onErpMetadataPatched?: (lineItemGroup: string, metadata: LineItemErpMetadata) => void
  onApprove?: () => void
  onSkip?: () => void
  onPrevious?: () => void
  pendingReviewCount?: number
  currentInvoiceIndex?: number
  totalInvoices?: number
  className?: string
}

export const InvoiceReviewSection = ({
  invoice: rawInvoice,
  isProcessing = false,
  onFieldUpdate,
  onFieldPatch,
  onInvoiceRefresh,
  onInvoiceRefreshSilent,
  onProcessingStarted,
  onRepresentationUpdate,
  onLineItemsUpdated,
  onErpMetadataPatched,
  onApprove,
  onSkip,
  onPrevious,
  pendingReviewCount = 0,
  currentInvoiceIndex,
  totalInvoices: providedTotalInvoices,
  className
}: InvoiceReviewSectionProps): JSX.Element => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const invoice = rawInvoice
  const invoiceId = invoice?.id

  const sessionId = searchParams.get("sessionId")
  const reviewSession = sessionId ? getReviewSession(sessionId) : null

  const sessionInitialTotal = reviewSession
    ? reviewSession.initialTotal > 0
      ? reviewSession.initialTotal
      : reviewSession.context.invoiceIds.length
    : null
  const sessionRemainingCount = reviewSession ? reviewSession.context.invoiceIds.length : null
  const sessionCompletedCount =
    reviewSession && sessionInitialTotal
      ? Math.min(reviewSession.completedCount, sessionInitialTotal)
      : 0

  const remainingInvoicesCount = sessionRemainingCount ?? pendingReviewCount

  const totalInvoices = useMemo(() => {
    if (sessionInitialTotal && sessionInitialTotal > 0) {
      return sessionInitialTotal
    }

    if (typeof providedTotalInvoices === "number" && providedTotalInvoices > 0) {
      return providedTotalInvoices
    }

    if (remainingInvoicesCount > 0) {
      return remainingInvoicesCount
    }

    return invoice ? 1 : 0
  }, [sessionInitialTotal, providedTotalInvoices, remainingInvoicesCount, invoice])

  const currentPosition = useMemo(() => {
    if (reviewSession && totalInvoices > 0) {
      const indexWithinSession =
        typeof currentInvoiceIndex === "number" && currentInvoiceIndex >= 0
          ? currentInvoiceIndex
          : 0
      const basePosition = sessionCompletedCount
      const computedPosition = basePosition + indexWithinSession + 1
      return Math.min(Math.max(computedPosition, 1), totalInvoices)
    }

    if (typeof currentInvoiceIndex === "number" && currentInvoiceIndex >= 0) {
      return currentInvoiceIndex + 1
    }

    if (totalInvoices > 0) {
      return 1
    }

    return 0
  }, [reviewSession, totalInvoices, currentInvoiceIndex, sessionCompletedCount])

  const zoom = useZoom(100, 50, 200, 25)
  const fileViewer = useFileViewer(invoice?.id)
  const fieldEditor = useFieldEditor()

  const handleLineItemsAction = useCallback(
    async (action: LineItemAction) => {
      if (!invoiceId) {
        const message = "Invoice data is not available."
        showErrorToast(message)
        throw new Error(message)
      }

      try {
        const response = await updateInvoiceLineItems(invoiceId, action.nextLineItems)
        if (!response.success) {
          throw new Error(response.message ?? "Failed to update line items.")
        }

        const successMessage =
          action.type === "merge"
            ? "Line items merged successfully."
            : action.type === "delete"
              ? "Line item removed successfully."
              : "Line item added successfully."
        showSuccessToast(successMessage)

        // Patch state in-place using returned data instead of full refetch
        if (onLineItemsUpdated && response.extracted_data) {
          onLineItemsUpdated(response.extracted_data, response.representation ?? null, response.additional_metadata)
        } else if (onInvoiceRefresh) {
          await Promise.resolve(onInvoiceRefresh())
        }
      } catch (error) {
        const fallbackMessage =
          action.type === "merge"
            ? "Failed to merge line items."
            : action.type === "delete"
              ? "Failed to remove line item."
              : "Failed to add line item."

        const message =
          error instanceof Error && error.message
            ? error.message
            : fallbackMessage

        showErrorToast(message)
        throw error
      }
    },
    [invoiceId, onLineItemsUpdated, onInvoiceRefresh]
  )

  const handleLineItemErpMetadataChange = useCallback(
    async (lineItemGroup: string, metadata: LineItemErpMetadata) => {
      if (!invoiceId) {
        showErrorToast("Invoice data is not available.")
        return
      }

      try {
        const response = await updateLineItemErpMetadata(invoiceId, lineItemGroup, metadata)
        if (!response.success) {
          throw new Error(response.message ?? "Failed to update line item ERP metadata.")
        }

        showSuccessToast("Line item ERP metadata updated.")
        showLearningToast(response.rule_generation_job_id ?? null)
        // Patch local state so erpMetadata prop stays fresh and sync effects work
        onErpMetadataPatched?.(lineItemGroup, metadata)
      } catch (error) {
        const message =
          error instanceof Error && error.message
            ? error.message
            : "Failed to update line item ERP metadata."
        showErrorToast(message)
      }
    },
    [invoiceId, onErpMetadataPatched]
  )

  const handleRestoreLineItems = useCallback(
    async (mergedGroupId: string) => {
      if (!invoiceId) {
        showErrorToast("Invoice data is not available.")
        return
      }

      try {
        const response = await restoreMergedLineItems(invoiceId, mergedGroupId)
        if (!response.success) {
          throw new Error(response.message ?? "Failed to restore merged line items.")
        }

        showSuccessToast("Merge undone — original line items restored.")

        if (onLineItemsUpdated && response.extracted_data) {
          onLineItemsUpdated(response.extracted_data, response.representation ?? null, response.additional_metadata)
        } else if (onInvoiceRefresh) {
          await Promise.resolve(onInvoiceRefresh())
        }
      } catch (error) {
        const message =
          error instanceof Error && error.message
            ? error.message
            : "Failed to restore merged line items."
        showErrorToast(message)
      }
    },
    [invoiceId, onLineItemsUpdated, onInvoiceRefresh]
  )

  const [showReprocessConfirm, setShowReprocessConfirm] = useState(false)
  const [isReprocessing, setIsReprocessing] = useState(false)
  const [showReturnToReviewConfirm, setShowReturnToReviewConfirm] = useState(false)
  const [isReturningToReview, setIsReturningToReview] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const childInvoiceCount = useMemo(() => {
    const childIds = invoice?.additional_metadata?.child_invoice_ids
    return Array.isArray(childIds) ? childIds.length : 0
  }, [rawInvoice])

  const handleReprocess = useCallback(async () => {
    if (!invoiceId) return

    setIsReprocessing(true)
    try {
      await reprocessInvoice(invoiceId)
      showSuccessToast("Invoice reprocessing started")
      setShowReprocessConfirm(false)
      if (onProcessingStarted) {
        onProcessingStarted()
      }
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : "Failed to reprocess invoice")
    } finally {
      setIsReprocessing(false)
    }
  }, [invoiceId, onProcessingStarted])

  const handleReturnToReview = useCallback(async () => {
    if (!invoiceId) return

    setIsReturningToReview(true)
    try {
      const response = await apiClient.fetch(`/invoices/${invoiceId}/return-to-review`, {
        method: 'POST',
      })

      if (response.ok) {
        showSuccessToast("Invoice returned to review queue")
        setShowReturnToReviewConfirm(false)
        if (onInvoiceRefresh) {
          await Promise.resolve(onInvoiceRefresh())
        }
      } else {
        const data = await response.json().catch(() => ({}))
        showErrorToast(data?.detail ?? `Failed to return invoice to review (${response.status})`)
      }
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : "Failed to return invoice to review")
    } finally {
      setIsReturningToReview(false)
    }
  }, [invoiceId, onInvoiceRefresh])

  const handleDelete = useCallback(async () => {
    if (!invoiceId) return
    setIsDeleting(true)
    try {
      const response = await apiClient.fetch(`/invoices/${invoiceId}`, { method: 'DELETE' })
      if (response.ok) {
        showSuccessToast("Invoice deleted successfully")
        setShowDeleteConfirm(false)
        navigate('/invoices/processing')
      } else {
        const data = await response.json().catch(() => ({}))
        showErrorToast(data?.detail ?? "Failed to delete invoice")
      }
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : "Failed to delete invoice")
    } finally {
      setIsDeleting(false)
    }
  }, [invoiceId, navigate])

  const emptyStateClass = className
    ? `flex flex-col w-full items-start gap-6 px-0 py-6 ${className}`
    : "flex flex-col w-full items-start gap-6 px-0 py-6"

  if (!invoice) {
    return (
      <section className={emptyStateClass}>
        <div className="w-full text-center py-12">
          <p className="text-gray-500">No invoice data available</p>
        </div>
      </section>
    )
  }

  const sectionClassName = className
    ? `flex w-full flex-col gap-4 px-0 min-h-screen ${className}`
    : "flex w-full flex-col gap-4 px-0 min-h-screen"

  const [lineItemsMissingLabels, setLineItemsMissingLabels] = useState<string[]>([])

  const vendorFieldsForDirection = useMemo(
    () => (invoice.extractedData ?? []).filter((f) => f.field_name.toLowerCase().startsWith("vendor_")),
    [invoice.extractedData]
  )
  const { isIncome } = useInvoiceType({
    client: invoice.client,
    vendorFields: vendorFieldsForDirection,
  })

  const missingRequiredFields = useMemo(() => {
    const valuesByName = new Map<string, string>()
    for (const f of invoice.extractedData ?? []) {
      const key = f.field_name.toLowerCase()
      const val = (f.field_value ?? "").toString().trim()
      // Prefer the first non-empty value if duplicates exist
      if (!valuesByName.has(key) || (valuesByName.get(key) === "" && val !== "")) {
        valuesByName.set(key, val)
      }
    }
    const missing: string[] = []
    REQUIRED_INVOICE_FIELDS.forEach((name) => {
      if ((valuesByName.get(name) ?? "") === "") missing.push(name)
    })

    // When no Mirage counterparty is matched, the user must fill in the
    // counterparty's name, tax id and country so it can be created downstream.
    // Direction (vendor vs customer) follows the invoice direction.
    const hasMirageCounterparty = invoice.mirage_counterparty_id != null
    if (!hasMirageCounterparty) {
      const prefix = isIncome ? "customer" : "vendor"
      for (const suffix of ["name", "tax_id", "country"]) {
        const name = `${prefix}_${suffix}`
        if ((valuesByName.get(name) ?? "") === "") missing.push(name)
      }
    }
    return missing
  }, [invoice.extractedData, invoice.mirage_counterparty_id, isIncome])

  const counterpartyFieldLabels: Record<string, string> = {
    vendor_name: "Vendor Name",
    vendor_tax_id: "Vendor Tax Id",
    vendor_country: "Vendor Country",
    customer_name: "Customer Name",
    customer_tax_id: "Customer Tax Id",
    customer_country: "Customer Country",
  }
  const combinedMissingLabels = [
    ...missingRequiredFields.map((n) => INVOICE_FIELD_LABELS[n] ?? counterpartyFieldLabels[n] ?? n),
    ...lineItemsMissingLabels,
  ]
  const approveDisabledReason = combinedMissingLabels.length > 0
    ? `Cannot approve. Missing required fields: ${combinedMissingLabels.join(", ")}`
    : undefined

  return (
    <section className={sectionClassName}>
      <header className="flex w-full flex-col gap-1">
        <InvoiceNavigationCard
          currentPosition={currentPosition}
          totalCount={totalInvoices}
          onSkip={onSkip}
          onApprove={onApprove}
          onPrevious={onPrevious}
          invoiceStatus={invoice?.status}
          onReprocess={() => setShowReprocessConfirm(true)}
          onReturnToReview={() => setShowReturnToReviewConfirm(true)}
          onDelete={() => setShowDeleteConfirm(true)}
          disabled={isProcessing}
          approveDisabled={combinedMissingLabels.length > 0}
          approveDisabledReason={approveDisabledReason}
        />
      </header>

      <main className="grid grid-cols-12 gap-4 w-full flex-1 min-h-0 transition-all duration-300">
        <DocumentViewer
          invoice={invoice}
          fileViewer={fileViewer}
          zoom={zoom}
        />

        <aside className="col-span-12 lg:col-span-6 flex h-full">
          {isProcessing ? (
            <div className="flex flex-col items-center justify-center w-full h-full min-h-[400px] bg-white border border-neutral-200 rounded-lg shadow-sm">
              <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4" />
              <p className="text-sm font-medium text-neutral-700">Processing invoice...</p>
              <p className="text-xs text-neutral-400 mt-1">Fields will appear once extraction is complete</p>
            </div>
          ) : (
            <div className="grid w-full h-full gap-4 overflow-y-auto auto-rows-min 2xl:grid-cols-2 2xl:content-start">
              {/* Row 1 Col 1: Client */}
              <div className="2xl:col-span-1">
                <ClientAssignmentCard
                  invoice={invoice}
                  onInvoiceRefresh={onInvoiceRefreshSilent ?? onInvoiceRefresh}
                  onFieldUpdate={onFieldUpdate}
                  fieldEditor={fieldEditor}
                  readOnly={invoice.status === "exported"}
                />
              </div>

              <InvoiceFieldsPanel
                invoice={invoice}
                onFieldUpdate={onFieldUpdate}
                onFieldPatch={onFieldPatch}
                onInvoiceRefresh={onInvoiceRefresh}
                onRepresentationUpdate={onRepresentationUpdate}
                fieldEditor={fieldEditor}
                onLineItemsAction={handleLineItemsAction}
                onLineItemErpMetadataChange={handleLineItemErpMetadataChange}
                onLineItemsValidityChange={setLineItemsMissingLabels}
                onRestoreLineItems={handleRestoreLineItems}
              />
            </div>
          )}
        </aside>
      </main>

      <div className="mt-4 w-full border-t border-neutral-200 bg-white py-4">
        <InvoiceNavigationCard
          currentPosition={currentPosition}
          totalCount={totalInvoices}
          onSkip={onSkip}
          onApprove={onApprove}
          onPrevious={onPrevious}
          invoiceStatus={invoice?.status}
          onReprocess={() => setShowReprocessConfirm(true)}
          onReturnToReview={() => setShowReturnToReviewConfirm(true)}
          onDelete={() => setShowDeleteConfirm(true)}
          disabled={isProcessing}
          approveDisabled={combinedMissingLabels.length > 0}
          approveDisabledReason={approveDisabledReason}
        />
      </div>

      <ReprocessConfirmDialog
        open={showReprocessConfirm}
        onConfirm={() => void handleReprocess()}
        onCancel={() => setShowReprocessConfirm(false)}
        isProcessing={isReprocessing}
        childrenCount={childInvoiceCount}
      />

      <DeleteConfirmDialog
        open={showDeleteConfirm}
        onConfirm={() => void handleDelete()}
        onCancel={() => setShowDeleteConfirm(false)}
        isDeleting={isDeleting}
      />

      <Dialog open={showReturnToReviewConfirm} onOpenChange={(open) => { if (!open) setShowReturnToReviewConfirm(false) }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full">
                <Undo2 className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <DialogTitle className="text-lg font-semibold text-gray-900">
                  Return to Review
                </DialogTitle>
              </div>
            </div>
          </DialogHeader>

          <div className="py-4">
            <p className="text-sm text-gray-700">
              This invoice will be returned to the review queue. All extracted data, corrections, and ERP metadata will be preserved. No data will be deleted.
            </p>
          </div>

          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setShowReturnToReviewConfirm(false)}
              disabled={isReturningToReview}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void handleReturnToReview()}
              disabled={isReturningToReview}
            >
              {isReturningToReview ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <Undo2 className="w-4 h-4 mr-2" />
              )}
              Return to Review
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  )
}
